import React from 'react';
import {
  Box,
  Button,
  Chip,
  Drawer,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Paper,
  Popover,
  Select,
  Switch,
  Typography,
} from '@mui/material';
import { CalendarToday as CalendarTodayIcon } from '@mui/icons-material';
import colors from '../../../assets/theme/colors';
import TimelineDateCalendar from '../../UI/TimelineDateCalendar';
import { renderQuickTagChipIcon, timelineUserRoleOptions } from './mobileCaptureDashboardUtils';

const MobileTimelineFilterSheet = ({
  open,
  onClose,
  onClear,
  importantOnly,
  onImportantOnlyChange,
  userRoles,
  onUserRolesChange,
  availableTags,
  selectedTagKeys,
  onToggleTag,
  datePickerAnchor,
  onOpenDatePicker,
  onCloseDatePicker,
  selectedDate,
  onSelectDate,
  entries,
}) => {
  const isCalendarOpen = Boolean(datePickerAnchor);

  return (
    <>
      <Drawer
        anchor="bottom"
        open={open}
        onClose={onClose}
        PaperProps={{
          sx: {
            borderRadius: '20px 20px 0 0',
            borderTop: `1px solid ${colors.landing.borderLight}`,
            bgcolor: colors.landing.surface,
            maxHeight: '72vh',
            pb: 'env(safe-area-inset-bottom)',
          },
        }}
      >
        <Box sx={{ px: 1.75, pt: 1.5, pb: 1.75 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 1.25 }}>
            <Box>
              <Typography sx={{ fontSize: '0.78rem', fontWeight: 800, letterSpacing: '0.14em', textTransform: 'uppercase', color: colors.landing.textMuted }}>
                Filters
              </Typography>
              <Typography sx={{ fontSize: '1rem', fontWeight: 800, color: colors.landing.heroText }}>
                Important moments and tags
              </Typography>
            </Box>
            <Button
              variant="text"
              onClick={onClear}
              sx={{
                textTransform: 'none',
                color: colors.landing.textMuted,
                fontWeight: 700,
              }}
            >
              Clear
            </Button>
          </Box>

          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1.25 }}>
            <FormControl size="small" sx={{ minWidth: 0, flex: '1 1 180px' }}>
              <InputLabel>Who logged it</InputLabel>
              <Select
                multiple
                value={userRoles}
                label="Who logged it"
                onChange={(event) => {
                  const value = event.target.value;
                  onUserRolesChange(typeof value === 'string' ? value.split(',') : value);
                }}
                renderValue={(selected) => (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {(Array.isArray(selected) ? selected : []).map((value) => {
                      const option = timelineUserRoleOptions.find((item) => item.value === value);
                      return (
                        <Chip key={value} label={option?.label || value} size="small" />
                      );
                    })}
                  </Box>
                )}
              >
                {timelineUserRoleOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Button
              variant="outlined"
              onClick={onOpenDatePicker}
              startIcon={<CalendarTodayIcon sx={{ fontSize: 16 }} />}
              sx={{
                minHeight: 40,
                flex: '1 1 180px',
                borderRadius: '12px',
                textTransform: 'none',
                fontWeight: 800,
                color: colors.landing.heroText,
                borderColor: colors.landing.borderLight,
                bgcolor: colors.landing.surface,
              }}
            >
              Jump to date
            </Button>
          </Box>

          <Paper
            variant="outlined"
            sx={{
              p: 1.25,
              borderRadius: '16px',
              borderColor: colors.landing.borderLight,
              bgcolor: colors.landing.sageLight,
              mb: 1.25,
            }}
          >
            <FormControlLabel
              control={
                <Switch
                  checked={importantOnly}
                  onChange={(event) => onImportantOnlyChange(event.target.checked)}
                  color="primary"
                />
              }
              label="Show only important / flagged"
              sx={{
                m: 0,
                alignItems: 'center',
                gap: 1,
                '& .MuiFormControlLabel-label': {
                  fontWeight: 700,
                  color: colors.landing.heroText,
                },
              }}
            />
          </Paper>

          <Typography sx={{ fontSize: '0.76rem', fontWeight: 800, letterSpacing: '0.14em', textTransform: 'uppercase', color: colors.landing.textMuted, mb: 0.9 }}>
            Tags
          </Typography>

          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 0.8,
              maxHeight: '32vh',
              overflowY: 'auto',
              pr: 0.25,
              pb: 0.5,
            }}
          >
            {availableTags.map((tag) => {
              const selected = selectedTagKeys.includes(tag.key);
              return (
                <Chip
                  key={tag.key}
                  label={tag.label}
                  icon={renderQuickTagChipIcon(tag)}
                  onClick={() => onToggleTag(tag.key)}
                  sx={{
                    flex: '0 0 auto',
                    height: 38,
                    borderRadius: 9999,
                    fontWeight: 800,
                    px: 1,
                    color: selected ? colors.landing.heroText : colors.landing.textMuted,
                    bgcolor: selected ? colors.landing.cyanPop : colors.landing.surface,
                    border: `1px solid ${selected ? colors.landing.cyanPop : colors.landing.borderLight}`,
                    '& .MuiChip-label': {
                      px: 1.1,
                    },
                    '& .MuiChip-icon': {
                      color: 'inherit',
                    },
                  }}
                />
              );
            })}
          </Box>

          <Box sx={{ display: 'flex', gap: 1, mt: 1.5 }}>
            <Button
              fullWidth
              variant="outlined"
              onClick={onClose}
              sx={{
                borderRadius: '12px',
                textTransform: 'none',
                fontWeight: 800,
                borderColor: colors.landing.borderLight,
                color: colors.landing.textMuted,
              }}
            >
              Close
            </Button>
            <Button
              fullWidth
              variant="contained"
              onClick={onClose}
              sx={{
                borderRadius: '12px',
                textTransform: 'none',
                fontWeight: 800,
                bgcolor: colors.brand.ink,
                color: colors.landing.heroText,
                '&:hover': {
                  bgcolor: colors.brand.navy,
                },
              }}
            >
              Done
            </Button>
          </Box>
        </Box>
      </Drawer>

      <Popover
        open={isCalendarOpen}
        anchorEl={datePickerAnchor}
        onClose={onCloseDatePicker}
        disableScrollLock
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        PaperProps={{
          sx: {
            borderRadius: '18px',
            border: `1px solid ${colors.landing.borderLight}`,
            boxShadow: `0 24px 60px ${colors.landing.shadowPanel}`,
            overflow: 'hidden',
          },
        }}
      >
        <TimelineDateCalendar
          value={selectedDate}
          onChange={(newDate) => {
            if (newDate) {
              onSelectDate(newDate);
            }
          }}
          entries={entries}
          allowFuture
          sx={{
            '& .MuiPickersCalendarHeader-label': {
              color: colors.landing.heroText,
              fontWeight: 800,
            },
            '& .MuiPickersDay-root.Mui-selected': {
              bgcolor: colors.landing.cyanPop,
              color: colors.landing.heroText,
            },
            '& .MuiPickersDay-root.Mui-selected:hover': {
              bgcolor: colors.landing.cyanPop,
            },
          }}
        />
      </Popover>
    </>
  );
};

export default MobileTimelineFilterSheet;
